import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { sharesAPI } from '../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { 
  FileText, 
  Download, 
  Building2, 
  User, 
  Mail, 
  MapPin,
  Calendar,
  Euro,
  Shield,
  AlertCircle,
  CheckCircle,
  Clock,
  FileSpreadsheet,
  Loader2,
  ExternalLink,
  Lock
} from 'lucide-react';
import { toast } from 'sonner';

const SharedInvoicePage = () => {
  const { token } = useParams();
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [downloadingPDF, setDownloadingPDF] = useState(false);
  const [downloadingCSV, setDownloadingCSV] = useState(false);

  useEffect(() => {
    loadInvoice();
  }, [token]);

  const loadInvoice = async () => {
    try {
      const response = await sharesAPI.getPublicInvoice(token);
      setInvoice(response.data);
    } catch (err) {
      console.error('Failed to load invoice:', err);
      const status = err.response?.status;
      if (status === 404) {
        setError({
          code: 'NOT_FOUND',
          title: 'Lien invalide',
          message: 'Ce lien de partage n\'existe pas ou a été supprimé.'
        });
      } else if (status === 403) {
        const detail = err.response?.data?.detail;
        if (detail?.includes('révoqué')) {
          setError({
            code: 'REVOKED',
            title: 'Lien révoqué',
            message: 'Ce lien a été révoqué par l\'émetteur de la facture.'
          });
        } else {
          setError({
            code: 'EXPIRED',
            title: 'Lien expiré',
            message: 'Ce lien de partage a expiré. Veuillez demander un nouveau lien à l\'émetteur.'
          });
        }
      } else {
        setError({
          code: 'ERROR',
          title: 'Erreur',
          message: 'Une erreur est survenue lors du chargement de la facture.'
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadCSV = async () => {
    setDownloadingCSV(true);
    try {
      const response = await sharesAPI.exportPublicCSV(token);
      const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `facture_${invoice.invoice_number}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('Export CSV téléchargé');
    } catch (err) {
      toast.error('Erreur lors du téléchargement');
    } finally {
      setDownloadingCSV(false);
    }
  };

  const handleDownloadPDF = async () => {
    setDownloadingPDF(true);
    try {
      const response = await sharesAPI.exportPublicPDF(token);
      if (response.data?.pdf_url) {
        window.open(response.data.pdf_url, '_blank');
        toast.success('PDF généré');
      }
    } catch (err) {
      toast.error('Erreur lors de la génération du PDF');
    } finally {
      setDownloadingPDF(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: 'long',
      year: 'numeric'
    });
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'paid':
        return (
          <Badge className="bg-emerald-100 text-emerald-700">
            <CheckCircle className="h-3 w-3 mr-1" />
            Payée
          </Badge>
        );
      case 'pending':
        return (
          <Badge className="bg-amber-100 text-amber-700">
            <Clock className="h-3 w-3 mr-1" />
            En attente
          </Badge>
        );
      case 'overdue':
        return (
          <Badge className="bg-red-100 text-red-700">
            <AlertCircle className="h-3 w-3 mr-1" />
            En retard
          </Badge>
        );
      default:
        return <Badge>{status}</Badge>;
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-10 w-10 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-slate-600">Chargement de la facture...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
        <Card className="max-w-md w-full shadow-xl">
          <CardContent className="p-8 text-center">
            <div className={`w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center ${
              error.code === 'EXPIRED' ? 'bg-amber-100' : 
              error.code === 'REVOKED' ? 'bg-red-100' : 'bg-slate-100'
            }`}>
              {error.code === 'EXPIRED' ? (
                <Clock className="h-8 w-8 text-amber-600" />
              ) : error.code === 'REVOKED' ? (
                <Lock className="h-8 w-8 text-red-600" />
              ) : (
                <AlertCircle className="h-8 w-8 text-slate-600" />
              )}
            </div>
            <h2 className="text-xl font-semibold text-slate-900 mb-2">
              {error.title}
            </h2>
            <p className="text-slate-500 mb-6">
              {error.message}
            </p>
            <Link to="/">
              <Button variant="outline">
                Retour à l'accueil
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-600 to-blue-500 flex items-center justify-center">
                <FileText className="h-5 w-5 text-white" />
              </div>
              <div>
                <h1 className="font-bold text-slate-900" style={{ fontFamily: "'Outfit', sans-serif" }}>
                  SmartFacture AI
                </h1>
                <p className="text-xs text-slate-500">Consultation sécurisée</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button 
                variant="outline" 
                onClick={handleDownloadCSV}
                disabled={downloadingCSV}
                data-testid="download-csv-btn"
              >
                {downloadingCSV ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <FileSpreadsheet className="h-4 w-4 mr-2" />
                )}
                CSV
              </Button>
              <Button 
                onClick={handleDownloadPDF}
                disabled={downloadingPDF}
                className="bg-blue-600 hover:bg-blue-700"
                data-testid="download-pdf-btn"
              >
                {downloadingPDF ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Download className="h-4 w-4 mr-2" />
                )}
                PDF
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Security Banner */}
      <div className="bg-emerald-50 border-b border-emerald-200">
        <div className="max-w-4xl mx-auto px-4 py-3">
          <div className="flex items-center gap-2 text-emerald-700 text-sm">
            <Shield className="h-4 w-4" />
            <span>
              <strong>Consultation sécurisée</strong> — Ce document vous a été partagé en lecture seule
            </span>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 py-8">
        <Card className="shadow-lg" data-testid="shared-invoice-card">
          <CardHeader className="border-b border-slate-100">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <CardTitle className="text-2xl text-slate-900">
                  Facture {invoice.invoice_number}
                </CardTitle>
                <p className="text-slate-500 mt-1">
                  Émise le {formatDate(invoice.created_at)}
                </p>
              </div>
              <div className="flex items-center gap-3">
                {getStatusBadge(invoice.status)}
                <div className="text-right">
                  <p className="text-3xl font-bold text-slate-900">
                    {invoice.grand_total?.toFixed(2)} <span className="text-lg">{invoice.currency}</span>
                  </p>
                </div>
              </div>
            </div>
          </CardHeader>

          <CardContent className="p-6">
            {/* Parties */}
            <div className="grid md:grid-cols-2 gap-6 mb-8">
              {/* Emitter */}
              <div className="bg-slate-50 rounded-xl p-5">
                <h3 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                  <Building2 className="h-4 w-4 text-blue-600" />
                  Émetteur
                </h3>
                <div className="space-y-2 text-sm">
                  {invoice.emitter_company && (
                    <p className="font-medium text-slate-900">{invoice.emitter_company}</p>
                  )}
                  {invoice.emitter_name && (
                    <p className="text-slate-600 flex items-center gap-2">
                      <User className="h-4 w-4 text-slate-400" />
                      {invoice.emitter_name}
                    </p>
                  )}
                  {invoice.emitter_siren && (
                    <p className="text-slate-600">SIREN: {invoice.emitter_siren}</p>
                  )}
                  {invoice.emitter_address && (
                    <p className="text-slate-600 flex items-center gap-2">
                      <MapPin className="h-4 w-4 text-slate-400" />
                      {invoice.emitter_address}
                    </p>
                  )}
                  {invoice.emitter_email && (
                    <p className="text-slate-600 flex items-center gap-2">
                      <Mail className="h-4 w-4 text-slate-400" />
                      {invoice.emitter_email}
                    </p>
                  )}
                </div>
              </div>

              {/* Client */}
              <div className="bg-slate-50 rounded-xl p-5">
                <h3 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                  <User className="h-4 w-4 text-emerald-600" />
                  Client
                </h3>
                <div className="space-y-2 text-sm">
                  <p className="font-medium text-slate-900">{invoice.client_name}</p>
                  {invoice.client_email && (
                    <p className="text-slate-600 flex items-center gap-2">
                      <Mail className="h-4 w-4 text-slate-400" />
                      {invoice.client_email}
                    </p>
                  )}
                  {invoice.client_siren && (
                    <p className="text-slate-600">SIREN: {invoice.client_siren}</p>
                  )}
                  {invoice.client_vat && (
                    <p className="text-slate-600">TVA Intracom: {invoice.client_vat}</p>
                  )}
                  {invoice.client_address && (
                    <p className="text-slate-600 flex items-center gap-2">
                      <MapPin className="h-4 w-4 text-slate-400" />
                      {invoice.client_address}
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Due Date */}
            <div className="flex items-center gap-2 mb-6 p-3 bg-amber-50 rounded-lg">
              <Calendar className="h-5 w-5 text-amber-600" />
              <span className="text-amber-800">
                <strong>Date d'échéance :</strong> {formatDate(invoice.due_date)}
              </span>
            </div>

            <Separator className="my-6" />

            {/* Items Table */}
            <h3 className="font-semibold text-slate-900 mb-4">Détail de la facture</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-200">
                    <th className="text-left py-3 px-4 font-medium text-slate-600">Description</th>
                    <th className="text-right py-3 px-4 font-medium text-slate-600">Qté</th>
                    <th className="text-right py-3 px-4 font-medium text-slate-600">Prix unit.</th>
                    <th className="text-right py-3 px-4 font-medium text-slate-600">TVA</th>
                    <th className="text-right py-3 px-4 font-medium text-slate-600">Total HT</th>
                  </tr>
                </thead>
                <tbody>
                  {invoice.items?.map((item, index) => {
                    const lineTotal = (item.quantity || 1) * (item.unit_price || 0);
                    return (
                      <tr key={index} className="border-b border-slate-100">
                        <td className="py-3 px-4 text-slate-900">{item.description}</td>
                        <td className="text-right py-3 px-4 text-slate-600">{item.quantity}</td>
                        <td className="text-right py-3 px-4 text-slate-600">
                          {item.unit_price?.toFixed(2)} €
                        </td>
                        <td className="text-right py-3 px-4 text-slate-600">
                          {item.tax_rate}%
                        </td>
                        <td className="text-right py-3 px-4 font-medium text-slate-900">
                          {lineTotal.toFixed(2)} €
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Totals */}
            <div className="mt-6 flex justify-end">
              <div className="w-full max-w-xs space-y-2">
                <div className="flex justify-between text-slate-600">
                  <span>Total HT</span>
                  <span>{invoice.total_amount?.toFixed(2)} {invoice.currency}</span>
                </div>
                <div className="flex justify-between text-slate-600">
                  <span>TVA</span>
                  <span>{invoice.tax_amount?.toFixed(2)} {invoice.currency}</span>
                </div>
                <Separator />
                <div className="flex justify-between text-lg font-bold text-slate-900">
                  <span>Total TTC</span>
                  <span>{invoice.grand_total?.toFixed(2)} {invoice.currency}</span>
                </div>
              </div>
            </div>

            {/* Notes */}
            {invoice.notes && (
              <div className="mt-8 p-4 bg-slate-50 rounded-lg">
                <h4 className="font-medium text-slate-900 mb-2">Notes</h4>
                <p className="text-slate-600 text-sm whitespace-pre-wrap">{invoice.notes}</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Footer */}
        <div className="mt-8 text-center text-sm text-slate-500">
          <p>
            Document généré via{' '}
            <Link to="/" className="text-blue-600 hover:underline">
              SmartFacture AI
            </Link>
            {' '}— Solution de facturation intelligente
          </p>
        </div>
      </main>
    </div>
  );
};

export default SharedInvoicePage;
